/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Define classroom list models at the module API boundary.
 * @state-owner none
 * @allowed-imports TypeScript standard library
 * @forbidden React; routes; API client
 * @public-api via modules/classrooms/index.ts
 * @side-effects none
 * @tests modules/classrooms/model/next-lesson.test.ts and module-contracts.test.ts
 */

export interface ClassroomSummary {
  id: string;
  name: string;
  subject: string;
  studentCount: number;
  joinCode: string;
  students: string[];
  revision: number;
  status: 'active' | 'archived';
  canEditBasicDetails: boolean;
  canManageLifecycle: boolean;
}

export interface ClassMemberSummary {
  id: string;
  userId: string;
  name: string;
  email: string | null;
  role: 'owner' | 'teacher' | 'student';
  status: 'active' | 'invited' | 'pending' | 'removed' | 'archived';
  lastSeenAt: string | null;
}

export interface ClassResourceSummary {
  id: string;
  name: string;
  kind: 'document' | 'exam';
  mimeType: string | null;
  sizeBytes: number | null;
  url: string | null;
  status: 'ready' | 'uploading' | 'parsing' | 'error';
}

export interface ClassActivitySummary {
  id: string;
  title: string;
  kind: 'assignment' | 'question' | 'material' | 'quiz';
  status: 'draft' | 'published' | 'archived';
  dueDate: string | null;
  updatedAt: string | null;
  progressLabel?: string;
  scoreLabel?: string;
}

interface ExistingClassApiResponse {
  id: string;
  name: string;
  subject?: string | null;
  student_count?: number;
  join_code: string;
  revision?: number;
  status?: 'active' | 'archived';
  can_edit_basic_details?: boolean;
  can_manage_lifecycle?: boolean;
}

export interface ExistingClassMemberResponse {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  student_name?: string;
  role: ClassMemberSummary['role'];
  status: ClassMemberSummary['status'];
  last_seen_at?: string | null;
}

function localizedSubject(subject: string | null | undefined): string {
  const normalized = subject?.trim();
  // Do not expose this known transport-error text as classroom content; actual directory failures
  // are represented by the query error state.
  if (!normalized || normalized.toLowerCase() === 'failed to retrieve tables') return '未設定科目';
  return normalized;
}

export function mapExistingClassResponse(value: ExistingClassApiResponse): ClassroomSummary {
  return {
    id: value.id,
    name: value.name,
    subject: localizedSubject(value.subject),
    studentCount: value.student_count ?? 0,
    joinCode: value.join_code,
    students: [],
    revision: value.revision ?? 1,
    status: value.status ?? 'active',
    canEditBasicDetails: value.can_edit_basic_details ?? false,
    canManageLifecycle: value.can_manage_lifecycle ?? false,
  };
}

export function mapExistingClassMemberResponse(
  value: ExistingClassMemberResponse,
): ClassMemberSummary {
  return {
    id: value.id,
    userId: value.user_id,
    name: value.student_name ?? value.user_name ?? value.user_email ?? '學生',
    email: value.user_email ?? null,
    role: value.role,
    status: value.status,
    lastSeenAt: value.last_seen_at ?? null,
  };
}
